import { LANDING } from "@/constants/testIds";

const IMAGES = {
  lobsterSubs: "/images/gallery-lobster-subs.jpg", // grilled lobster tail + seafood subs
  elote: "/images/gallery-charcuterie.jpg", // spicy street corn
  chefSpread: "/images/gallery-yogurt.jpg", // chef behind full brunch spread
  charcuterie: "/images/gallery-corn.jpg", // charcuterie & fruit board
  yogurtBowl: "/images/gallery-brunch.jpg", // yogurt bowl w/ berries + pistachio
  brunchPancakes: "/images/gallery-brunch-pancakes.jpg", // chocolate chip pancakes + brunch
  potatoes: "/images/kristy-portrait.jpg", // roasted potatoes + veg medley
};

export default function Gallery() {
  return (
    <section
      id="gallery"
      data-testid={LANDING.gallerySection}
      className="relative bg-[#F4F1EA] py-24 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal flex items-center gap-3 mb-8">
          <span className="h-px w-8 bg-[#C05A44]" />
          <span className="font-mono-editorial text-[11px] text-[#C05A44]">
            03 · FROM THE KITCHEN
          </span>
        </div>

        <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <h2 className="font-display font-light text-[#1A2B3C] leading-[1.02] text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl">
            Plated
            <span className="italic"> stories</span> from
            <br />
            her table.
          </h2>
          <p className="font-body text-[#5C6B7A] max-w-sm text-base md:text-lg leading-relaxed">
            A small taste of recent tables, dishes and destinations.
          </p>
        </div>

        {/* Tetris bento grid — real Kristy photography */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[220px] md:auto-rows-[240px]">
          {/* Signature hero — Bahamian lobster pasta */}
          <figure className="reveal relative overflow-hidden md:col-span-7 md:row-span-2 group">
            <img
              src={IMAGES.lobsterSubs}
              alt="Whole grilled Bahamian lobster over lemon-herb pasta"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              BAHAMIAN LOBSTER · SIGNATURE
            </figcaption>
          </figure>

          {/* Elote */}
          <figure className="reveal relative overflow-hidden md:col-span-5 group" style={{ transitionDelay: "80ms" }}>
            <img
              src={IMAGES.elote}
              alt="Spicy street corn with jalapeño and herbs"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              ISLAND ELOTE
            </figcaption>
          </figure>

          {/* Chef at the brunch spread */}
          <figure className="reveal relative overflow-hidden md:col-span-5 group" style={{ transitionDelay: "140ms" }}>
            <img
              src={IMAGES.chefSpread}
              alt="Chef Kristy Worrell hosting a full brunch spread"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              PRIVATE BRUNCH · IN-VILLA
            </figcaption>
          </figure>

          {/* Charcuterie */}
          <figure className="reveal relative overflow-hidden md:col-span-4 group" style={{ transitionDelay: "220ms" }}>
            <img
              src={IMAGES.charcuterie}
              alt="Charcuterie board with grapes, cherries and blackberries"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              GRAZING BOARD
            </figcaption>
          </figure>

          {/* Yogurt bowl */}
          <figure className="reveal relative overflow-hidden md:col-span-3 group" style={{ transitionDelay: "280ms" }}>
            <img
              src={IMAGES.yogurtBowl}
              alt="Pistachio yogurt bowl with raspberries, pear and granola"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              PISTACHIO YOGURT BOWL
            </figcaption>
          </figure>

          {/* Editorial sage tile */}
          <figure className="reveal relative overflow-hidden md:col-span-2 bg-[#4A5D4E] flex items-center justify-center" style={{ transitionDelay: "340ms" }}>
            <div className="text-center px-3">
              <div className="font-display italic text-[#F4F1EA] text-2xl leading-tight">
                Sourced daily
              </div>
              <div className="font-mono-editorial text-[9px] text-[#F4F1EA]/70 mt-2">
                ISLAND MARKETS
              </div>
            </div>
          </figure>

          {/* Brunch pancakes spread */}
          <figure className="reveal relative overflow-hidden md:col-span-3 group" style={{ transitionDelay: "400ms" }}>
            <img
              src={IMAGES.brunchPancakes}
              alt="Chocolate chip pancakes with tropical fruit, jams and yogurt"
              className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              loading="lazy"
            />
            <figcaption className="absolute left-4 bottom-4 font-mono-editorial text-[10px] tracking-widest text-[#F4F1EA] bg-[#1A2B3C]/55 px-3 py-1.5">
              SUNRISE SPREAD
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}
